import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { ThemeProvider as MuiThemeProvider, Theme } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import {
  createClinicalTheme,
  getStoredThemeMode,
  setStoredThemeMode,
} from "./constants";

interface ThemeContextType {
  mode: "light" | "dark";
  toggleTheme: () => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ClinicalThemeProvider");
  }
  return context;
};

interface ClinicalThemeProviderProps {
  children: ReactNode;
}

export const ClinicalThemeProvider: React.FC<ClinicalThemeProviderProps> = ({
  children,
}) => {
  const [mode, setMode] = useState<"light" | "dark">("light");
  const [theme, setTheme] = useState<Theme>(createClinicalTheme("light"));

  useEffect(() => {
    // Load theme mode from localStorage or system preference
    const storedMode = getStoredThemeMode();
    setMode(storedMode);
    setTheme(createClinicalTheme(storedMode));
  }, []);

  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    setTheme(createClinicalTheme(newMode));
    setStoredThemeMode(newMode);

    // Update CSS custom properties for dark mode
    document.documentElement.className = newMode === "dark" ? "dark" : "";
  };

  const contextValue: ThemeContextType = {
    mode,
    toggleTheme,
    theme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
